import React, { useState } from "react";
import { formatDateForAPI } from "@/lib/date-utils";
import { AlertTriangle, X } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ServerPaginatedTable } from "@/components/common/server-paginated-table";
import { GlobalFilterBar } from "@/components/common/global-filter-bar";
import { orphanAPI } from "@/api";
import { usePaginatedApi, useToastHelpers } from "@/hooks";
import { useDateRange } from "@/contexts/DateRangeContext";
import { formatDateTime } from "@/lib/utils";
import { ZoneOrphanTimeseriesResponse, ZoneOrphanDowResponse } from "@/types";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const ZONE_COLORS = [
  "#E86E30",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#f97316",
  "#0ea5e9",
  "#facc15",
];

const DOW_SHORT_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WINDOW_OPTIONS = [30, 60];

const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`;
const formatDelta = (value: number) =>
  `${value >= 0 ? "+" : ""}${(value * 100).toFixed(1)}%`;
const formatInteger = (value: number) => value.toLocaleString();

const rateToColor = (rate: number) => {
  const clamped = Math.min(Math.max(rate, 0), 0.5);
  const hue = 120 - (clamped / 0.5) * 120;
  const lightness = clamped > 0.25 ? 40 : 55;
  return `hsl(${Math.round(hue)}, 85%, ${lightness}%)`;
};

const rateTextColor = (rate: number) => (rate > 0.22 ? "#f8fafc" : "#0f172a");

export function Orphans() {
  const { success, error: showError } = useToastHelpers();
  const { dateRange, selectedLocations } = useDateRange();
  const [entriesPage, setEntriesPage] = useState(1);
  const [entriesPageSize, setEntriesPageSize] = useState(50);
  const [exitsPage, setExitsPage] = useState(1);
  const [exitsPageSize, setExitsPageSize] = useState(50);
  const [zoneTimeseries, setZoneTimeseries] =
    useState<ZoneOrphanTimeseriesResponse | null>(null);
  const [zoneTimeseriesLoading, setZoneTimeseriesLoading] = useState(false);
  const [zoneDow, setZoneDow] = useState<ZoneOrphanDowResponse | null>(null);
  const [zoneDowLoading, setZoneDowLoading] = useState(false);
  const [selectedZoneIds, setSelectedZoneIds] = useState<string[]>([]);
  const [windowDays, setWindowDays] = useState<number>(60);

  // Fetch orphaned entries
  const {
    data: orphanEntries,
    pagination: entriesPagination,
    isLoading: entriesLoading,
    refresh: refreshEntries,
    updateParams: updateEntriesParams,
  } = usePaginatedApi(orphanAPI.getOrphanEntries, {
    page: entriesPage,
    per_page: entriesPageSize,
    start_date: formatDateForAPI(dateRange.from),
    end_date: formatDateForAPI(dateRange.to),
    ...(selectedLocations.length > 0 && {
      location: selectedLocations.join(","),
    }),
  });

  // Fetch orphaned exits
  const {
    data: orphanExits,
    pagination: exitsPagination,
    isLoading: exitsLoading,
    refresh: refreshExits,
    updateParams: updateExitsParams,
  } = usePaginatedApi(orphanAPI.getOrphanExits, {
    page: exitsPage,
    per_page: exitsPageSize,
    start_date: formatDateForAPI(dateRange.from),
    end_date: formatDateForAPI(dateRange.to),
    ...(selectedLocations.length > 0 && {
      location: selectedLocations.join(","),
    }),
  });

  // Re-fetch when global filters change
  React.useEffect(() => {
    updateEntriesParams({
      page: 1,
      per_page: entriesPageSize,
      start_date: formatDateForAPI(dateRange.from),
      end_date: formatDateForAPI(dateRange.to),
      ...(selectedLocations.length > 0 && {
        location: selectedLocations.join(","),
      }),
    });
    setEntriesPage(1);
  }, [dateRange, selectedLocations, entriesPageSize]);

  React.useEffect(() => {
    updateExitsParams({
      page: 1,
      per_page: exitsPageSize,
      start_date: formatDateForAPI(dateRange.from),
      end_date: formatDateForAPI(dateRange.to),
      ...(selectedLocations.length > 0 && {
        location: selectedLocations.join(","),
      }),
    });
    setExitsPage(1);
  }, [dateRange, selectedLocations, exitsPageSize]);

  React.useEffect(() => {
    let isActive = true;

    const fetchTimeseries = async () => {
      if (!dateRange?.from || !dateRange?.to) return;
      setZoneTimeseriesLoading(true);
      try {
        const params: Record<string, string | number> = {
          start_date: formatDateForAPI(dateRange.from),
          end_date: formatDateForAPI(dateRange.to),
          window_days: windowDays,
        };
        if (selectedLocations.length > 0) {
          params.zones = selectedLocations.join(",");
        }

        const response = await orphanAPI.getZoneTimeseries(params);
        if (!isActive) return;

        const payload = response.data;
        setZoneTimeseries(payload);

        const availableIds = new Set(
          payload.available_zones.map((zone) => zone.zone_id),
        );

        setSelectedZoneIds((prev) => {
          const filtered = prev.filter((id) => availableIds.has(id));
          if (filtered.length) {
            return filtered.length === prev.length ? prev : filtered;
          }

          const defaults = payload.available_zones
            .slice(0, Math.min(4, payload.available_zones.length))
            .map((zone) => zone.zone_id);

          return defaults.length ? defaults : [];
        });
      } catch (err: any) {
        if (isActive) {
          showError(
            "Unable to load zone timeline data",
            err?.message ?? "Unexpected error",
          );
        }
      } finally {
        if (isActive) {
          setZoneTimeseriesLoading(false);
        }
      }
    };

    fetchTimeseries();

    return () => {
      isActive = false;
    };
  }, [dateRange, selectedLocations, windowDays, showError]);

  React.useEffect(() => {
    let isActive = true;

    const fetchDow = async () => {
      if (!dateRange?.from || !dateRange?.to) return;
      setZoneDowLoading(true);
      try {
        const params: Record<string, string | number> = {
          start_date: formatDateForAPI(dateRange.from),
          end_date: formatDateForAPI(dateRange.to),
          window_days: windowDays,
        };
        if (selectedLocations.length > 0) {
          params.zones = selectedLocations.join(",");
        }

        const response = await orphanAPI.getZoneDayOfWeek(params);
        if (!isActive) return;

        setZoneDow(response.data);
      } catch (err: any) {
        if (isActive) {
          showError(
            "Unable to load day-of-week metrics",
            err?.message ?? "Unexpected error",
          );
        }
      } finally {
        if (isActive) {
          setZoneDowLoading(false);
        }
      }
    };

    fetchDow();

    return () => {
      isActive = false;
    };
  }, [dateRange, selectedLocations, windowDays, showError]);

  const availableZones = zoneTimeseries?.available_zones ?? [];

  const selectedZoneSeries = React.useMemo(() => {
    if (!zoneTimeseries) return [];
    const fallback = zoneTimeseries.available_zones.map((zone) => zone.zone_id);
    const baseSelection = selectedZoneIds.length ? selectedZoneIds : fallback;
    const selectionSet = new Set(baseSelection);
    return zoneTimeseries.zones.filter((zone) =>
      selectionSet.has(zone.zone_id),
    );
  }, [zoneTimeseries, selectedZoneIds]);

  const chartData = React.useMemo(() => {
    if (!selectedZoneSeries.length) return [];
    return selectedZoneSeries[0].daily.map((_, index) => {
      const point: Record<string, any> = {
        date: selectedZoneSeries[0].daily[index].date,
      };

      selectedZoneSeries.forEach((zone) => {
        const zonePoint = zone.daily[index];
        if (!zonePoint) return;
        const keyBase = `zone_${zone.zone_id}`;
        point[keyBase] = zonePoint.total_orphans;
        point[`${keyBase}_rate`] = zonePoint.orphan_rate;
        point[`${keyBase}_entries`] = zonePoint.total_entries;
        point[`${keyBase}_exits`] = zonePoint.total_exits;
      });

      return point;
    });
  }, [selectedZoneSeries]);

  const selectedDowSeries = React.useMemo(() => {
    if (!zoneDow) return [];
    const fallback = zoneDow.zones.map((zone) => zone.zone_id);
    const baseSelection = selectedZoneIds.length ? selectedZoneIds : fallback;
    const selectionSet = new Set(baseSelection);
    return zoneDow.zones.filter((zone) => selectionSet.has(zone.zone_id));
  }, [zoneDow, selectedZoneIds]);

  const toggleZone = (zoneId: string) => {
    setSelectedZoneIds((prev) => {
      if (prev.includes(zoneId)) {
        if (prev.length === 1) {
          return prev;
        }
        return prev.filter((id) => id !== zoneId);
      }
      return [...prev, zoneId];
    });
  };

  const selectAllZones = () => {
    if (!availableZones.length) return;
    const allIds = availableZones.map((zone) => zone.zone_id);
    setSelectedZoneIds((prev) =>
      prev.length === allIds.length && prev.every((id) => allIds.includes(id))
        ? prev
        : allIds,
    );
  };

  const clearZoneSelection = () => {
    if (!availableZones.length) {
      setSelectedZoneIds([]);
      return;
    }
    const [firstZone] = availableZones;
    setSelectedZoneIds([firstZone.zone_id]);
  };

  const renderTooltip = React.useCallback(({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const dateLabel = new Date(label).toLocaleDateString();

    return (
      <div className="rounded-lg border border-border bg-card p-3 shadow-lg">
        <div className="text-sm font-semibold">{dateLabel}</div>
        <div className="mt-2 space-y-1 text-sm">
          {payload.map((item: any) => {
            const rate = item.payload?.[`${item.dataKey}_rate`] ?? 0;
            return (
              <div
                key={item.dataKey}
                className="flex items-center justify-between gap-4"
              >
                <span className="flex items-center gap-2">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  {item.name}
                </span>
                <span className="font-medium">
                  {formatInteger(item.value)} ({formatPercent(rate)})
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }, []);

  const handleDismissEntry = async (orphanId: string) => {
    try {
      await orphanAPI.dismissOrphan(orphanId);
      success("Orphan entry dismissed");
      refreshEntries();
    } catch (err: any) {
      showError("Dismiss failed", err.message);
    }
  };

  const handleDismissExit = async (orphanId: string) => {
    try {
      await orphanAPI.dismissOrphan(orphanId);
      success("Orphan exit dismissed");
      refreshExits();
    } catch (err: any) {
      showError("Dismiss failed", err.message);
    }
  };

  // Columns for orphaned entries
  const entriesColumns = [
    {
      id: "license_plate",
      header: "License Plate",
      accessorKey: "license_plate",
      cell: ({ row }: any) => (
        <span className="font-mono font-medium">
          {row.original.license_plate}
        </span>
      ),
    },
    {
      id: "entry_time",
      header: "Entry Time",
      accessorKey: "entry_time",
      cell: ({ row }: any) => formatDateTime(row.original.entry_time),
    },
    {
      id: "camera",
      header: "Camera",
      accessorKey: "camera",
    },
    {
      id: "location",
      header: "Location",
      accessorKey: "location",
    },
    {
      id: "confidence",
      header: "Confidence",
      accessorKey: "confidence",
      cell: ({ row }: any) => `${(row.original.confidence * 100).toFixed(1)}%`,
    },
    {
      id: "age",
      header: "Age",
      accessorKey: "age_hours",
      cell: ({ row }: any) => {
        const hours = row.original.age_hours;
        if (hours < 24) return `${hours}h`;
        return `${Math.floor(hours / 24)}d ${hours % 24}h`;
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => (
        <Button
          size="sm"
          variant="destructive"
          onClick={() => handleDismissEntry(row.original.id)}
        >
          <X className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  // Columns for orphaned exits
  const exitsColumns = [
    {
      id: "license_plate",
      header: "License Plate",
      accessorKey: "license_plate",
      cell: ({ row }: any) => (
        <span className="font-mono font-medium">
          {row.original.license_plate}
        </span>
      ),
    },
    {
      id: "exit_time",
      header: "Exit Time",
      accessorKey: "exit_time",
      cell: ({ row }: any) => formatDateTime(row.original.exit_time),
    },
    {
      id: "camera",
      header: "Camera",
      accessorKey: "camera",
    },
    {
      id: "location",
      header: "Location",
      accessorKey: "location",
    },
    {
      id: "confidence",
      header: "Confidence",
      accessorKey: "confidence",
      cell: ({ row }: any) => `${(row.original.confidence * 100).toFixed(1)}%`,
    },
    {
      id: "age",
      header: "Age",
      accessorKey: "age_hours",
      cell: ({ row }: any) => {
        const hours = row.original.age_hours;
        if (hours < 24) return `${hours}h`;
        return `${Math.floor(hours / 24)}d ${hours % 24}h`;
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => (
        <Button
          size="sm"
          variant="destructive"
          onClick={() => handleDismissExit(row.original.id)}
        >
          <X className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <AlertTriangle className="h-8 w-8 text-yellow-500" />
          <div>
            <h1 className="text-3xl font-bold">Orphaned Records</h1>
            <p className="text-muted-foreground">
              Unmatched entry and exit events
            </p>
          </div>
        </div>
      </div>

      {/* Global Filters */}
      <GlobalFilterBar />

      {/* Stats Banner */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-600">
                {entriesPagination?.total || 0}
              </div>
              <div className="text-sm text-muted-foreground">
                Orphaned Entries
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600">
                {exitsPagination?.total || 0}
              </div>
              <div className="text-sm text-muted-foreground">
                Orphaned Exits
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Zone timeline visualization */}
      <Card>
        <CardHeader className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Zone Orphan Trends</CardTitle>
              <CardDescription>
                Daily orphan totals across {windowDays}-day window for selected
                zones.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {WINDOW_OPTIONS.map((days) => (
                <Button
                  key={days}
                  size="sm"
                  variant={windowDays === days ? "default" : "outline"}
                  onClick={() => setWindowDays(days)}
                >
                  {days}d
                </Button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {availableZones.map((zone) => {
              const isSelected = selectedZoneIds.includes(zone.zone_id);
              return (
                <Button
                  key={zone.zone_id}
                  size="sm"
                  variant={isSelected ? "default" : "outline"}
                  onClick={() => toggleZone(zone.zone_id)}
                >
                  {zone.zone_label}
                </Button>
              );
            })}
            {availableZones.length > 0 && (
              <>
                <Button size="sm" variant="ghost" onClick={selectAllZones}>
                  Select All
                </Button>
                <Button size="sm" variant="ghost" onClick={clearZoneSelection}>
                  Clear
                </Button>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {zoneTimeseriesLoading ? (
            <div className="flex h-72 items-center justify-center text-sm text-muted-foreground">
              Loading zone metrics...
            </div>
          ) : chartData.length > 0 ? (
            <div className="h-80 w-full">
              <ResponsiveContainer>
                <RechartsLineChart
                  data={chartData}
                  margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) =>
                      new Date(value).toLocaleDateString()
                    }
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip content={renderTooltip} />
                  <Legend />
                  {selectedZoneSeries.map((zone, index) => (
                    <Line
                      key={zone.zone_id}
                      type="monotone"
                      dataKey={`zone_${zone.zone_id}`}
                      name={zone.zone_label}
                      stroke={ZONE_COLORS[index % ZONE_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </RechartsLineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex h-72 items-center justify-center text-sm text-muted-foreground">
              No orphan timeline data for the selected zones.
            </div>
          )}
        </CardContent>
      </Card>

      {selectedZoneSeries.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {selectedZoneSeries.map((zone) => {
            const snapshot = zone.snapshot;
            const rateChangeClass =
              snapshot.rate_change >= 0 ? "text-red-500" : "text-emerald-500";

            return (
              <Card key={zone.zone_id} className="border-border/60">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{zone.zone_label}</CardTitle>
                  <CardDescription>
                    Last 7 days: {formatInteger(snapshot.last_7_days)} orphaned
                    reads
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Current rate
                    </span>
                    <span className="text-base font-semibold">
                      {formatPercent(snapshot.current_rate)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Δ vs prior 7 days
                    </span>
                    <span
                      className={`text-base font-semibold ${rateChangeClass}`}
                    >
                      {formatDelta(snapshot.rate_change)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Total orphans ({windowDays}d)
                    </span>
                    <span className="text-base font-semibold">
                      {formatInteger(snapshot.total_orphans)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Total reads
                    </span>
                    <span className="text-base font-semibold">
                      {formatInteger(snapshot.total_reads)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Day-of-Week Hotspots</CardTitle>
          <CardDescription>
            Average orphan rate by day across the selected window.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {zoneDowLoading ? (
            <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
              Loading day-of-week metrics...
            </div>
          ) : selectedDowSeries.length > 0 ? (
            <div className="space-y-3">
              <div className="grid grid-cols-8 gap-2 text-xs font-medium text-muted-foreground">
                <div />
                {DOW_SHORT_LABELS.map((label) => (
                  <div
                    key={label}
                    className="text-center uppercase tracking-wide"
                  >
                    {label}
                  </div>
                ))}
              </div>
              {selectedDowSeries.map((series) => (
                <div
                  key={series.zone_id}
                  className="grid grid-cols-8 items-center gap-2"
                >
                  <div className="text-sm font-semibold">
                    {series.zone_label}
                  </div>
                  {series.data.map((point) => (
                    <div
                      key={`${series.zone_id}-${point.day_index}`}
                      className="rounded-md p-2 text-center transition-colors"
                      style={{
                        backgroundColor: rateToColor(point.orphan_rate),
                        color: rateTextColor(point.orphan_rate),
                      }}
                    >
                      <div className="text-xs font-semibold">
                        {formatPercent(point.orphan_rate)}
                      </div>
                      <div className="text-[11px] opacity-90">
                        {formatInteger(point.total_orphans)}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
              No day-of-week patterns available for the selected zones.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detailed Tables */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left Panel: Orphaned Entries */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <Badge variant="default">Entries</Badge>
                <span>No Matching Exit</span>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ServerPaginatedTable
              columns={entriesColumns}
              data={orphanEntries || []}
              pagination={entriesPagination}
              onPageChange={(page) => {
                setEntriesPage(page);
                updateEntriesParams({
                  page,
                  per_page: entriesPageSize,
                  start_date: formatDateForAPI(dateRange.from),
                  end_date: formatDateForAPI(dateRange.to),
                  ...(selectedLocations.length > 0 && {
                    location: selectedLocations.join(","),
                  }),
                });
              }}
              onPageSizeChange={(newPageSize) => {
                setEntriesPageSize(newPageSize);
                setEntriesPage(1);
                updateEntriesParams({
                  page: 1,
                  per_page: newPageSize,
                  start_date: formatDateForAPI(dateRange.from),
                  end_date: formatDateForAPI(dateRange.to),
                  ...(selectedLocations.length > 0 && {
                    location: selectedLocations.join(","),
                  }),
                });
              }}
              loading={entriesLoading}
            />
          </CardContent>
        </Card>

        {/* Right Panel: Orphaned Exits */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <Badge variant="secondary">Exits</Badge>
                <span>No Matching Entry</span>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ServerPaginatedTable
              columns={exitsColumns}
              data={orphanExits || []}
              pagination={exitsPagination}
              onPageChange={(page) => {
                setExitsPage(page);
                updateExitsParams({
                  page,
                  per_page: exitsPageSize,
                  start_date: formatDateForAPI(dateRange.from),
                  end_date: formatDateForAPI(dateRange.to),
                  ...(selectedLocations.length > 0 && {
                    location: selectedLocations.join(","),
                  }),
                });
              }}
              onPageSizeChange={(newPageSize) => {
                setExitsPageSize(newPageSize);
                setExitsPage(1);
                updateExitsParams({
                  page: 1,
                  per_page: newPageSize,
                  start_date: formatDateForAPI(dateRange.from),
                  end_date: formatDateForAPI(dateRange.to),
                  ...(selectedLocations.length > 0 && {
                    location: selectedLocations.join(","),
                  }),
                });
              }}
              loading={exitsLoading}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
